import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  initialKeywords,
  MEDIA_TYPE,
  searchEngines,
} from "../../../../constants/segmentTools";
import {
  usePostMediaBrowserDataV2,
  useSaveExternalImage,
} from "../../../../services/mutations";
import EmptySearchContainer from "../EmptySearchContainer";
import InitialView from "../InitialView";
import MediaMasonry from "./MediaMasonry";
import * as Styled from "./styles";
import { saveRecentMedia } from "../../../../services/api";
import { useSelector } from "react-redux";
import { getWheelId } from "../../../../redux/selectors/segmentBuilderSelectors";
import Header from "./Header";
import { getRandomId } from "../../../../utils/helpers";

const MediaBrowser = ({
  mediaType = "image",
  onConfirm = () => {},
  isGifAllowed,
}) => {
  const { mutate, isLoading, data } = usePostMediaBrowserDataV2();
  const saveExternalImageMutation = useSaveExternalImage();
  const [selectedEngine, setSelectedEngine] = useState(
    searchEngines[mediaType][0].key
  );
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [mediaKeyword, setMediaKeyword] = useState("");
  const [isNotFound, setIsNotFound] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingMediaItems, setLoadingMediaItems] = useState(new Set());
  const containerRef = useRef(null);

  const SEARCH_LENGTH_LIMIT = 2;
  const wheelId = useSelector(getWheelId);

  const isGif =
    isGifAllowed && selectedEngine === searchEngines[mediaType][1]?.key;

  const showDropdown = searchEngines[mediaType]?.length > 1 && isGifAllowed;

  const handleSearch = () => {
    // Trigger a search using current keyword and engine
    resetState();
    if (mediaKeyword.length > SEARCH_LENGTH_LIMIT) getMedia(mediaKeyword);
  };

  useEffect(() => {
    if (data?.data?.data) {
      setIsNotFound(!isLoading && data?.data?.data.length === 0);
      if (data?.data?.data.length > 0) {
        setSelectedMedia((prevMedia) => [
          ...prevMedia,
          ...data.data.data.map((d) => {
            if (isGif) {
              return {
                url: d.images.fixed_height_small.url,
                thumbnail:
                  d.images.fixed_height_small_still.url ||
                  d.images.original.url,
              };
            }
            return {
              url: d.largeImageURL || d.url,
              thumbnail: d.thumb,
            };
          }),
        ]);
      }
    }
  }, [data, isGif, isLoading]);

  const getMedia = (
    text = "",
    source = selectedEngine,
    page = 1,
    per_page = 20
  ) => {
    let params = {
      text,
      source: isGif ? 1 : source,
      type: isGif ? MEDIA_TYPE.gif : MEDIA_TYPE[mediaType],
    };

    if (isGif) {
      // For GIFs, calculate offset and limit
      params.offset = (page - 1) * per_page; // Offset is (page - 1) * per_page
      params.limit = per_page; // Limit is per_page
    } else {
      // Default: use page and per_page for other media types
      params.page = page;
      params.per_page = per_page;
    }

    // Now mutate with the updated params (either with page/per_page or offset/limit)
    mutate(params);
  };

  const resetState = () => {
    setPage(1);
    setSelectedMedia([]);
    setIsNotFound(false);
  };

  const handleMediaKeywordChange = (val) => {
    setMediaKeyword(val);
    // Do not search on type; search is triggered via search button or Enter key
    // Keep state ready; actual API call happens in handleSearch
    if (!val) {
      // If cleared, also clear stored media data and reset paging
      resetState();
    }
  };

  const handleInitialKeywordsClick = (val) => {
    setMediaKeyword(val);
    resetState();
    if (val.length > SEARCH_LENGTH_LIMIT) getMedia(val);
  };

  const handleEngineChange = (val) => {
    setSelectedEngine(val);
    resetState();
    setMediaKeyword("");
    // if (mediaKeyword.length > SEARCH_LENGTH_LIMIT) getMedia(mediaKeyword, val);
  };
  const getFileType = () => {
    switch (mediaType) {
      case "images":
        return "0";
      case "image":
        return "0";
      case "video":
        return "1";
      case "documents":
        return "2";
      case "audio":
        return "3";
    }
  };

  const handleSave = async (data) => {
    const mediaUrl = data.url || data.src;

    // Add media item to loading set
    setLoadingMediaItems((prev) => new Set([...prev, mediaUrl]));

    try {
      let finalData = { ...data };
      const id = getRandomId();

      // For images, first save the external image to get a persisted URL
      if (mediaType === "image" || mediaType === "images") {
        onConfirm({ id, loading: true });
        const saveResponse = await saveExternalImageMutation.mutateAsync({
          url: mediaUrl,
          wheel_id: wheelId,
        });

        // Use the URL returned from the API
        finalData.src = saveResponse?.data?.data?.url;
      } else {
        // For non-images, use the original URL
        finalData.src = mediaUrl;
      }

      // Continue with the normal flow
      onConfirm({ ...finalData, id, loading: false });

      await saveRecentMedia(wheelId, {
        type: getFileType(),
        url: finalData.src || data.src,
        metadata: {
          type: mediaType === "video" ? "youtube" : "",
          thumbnail: data.thumbnail,
        },
      });
    } catch (error) {
      console.error("Error saving media:", error);
      // Still call onConfirm with original data if API call fails
      onConfirm({ ...data, loading: false });
    } finally {
      // Remove media item from loading set
      setLoadingMediaItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(mediaUrl);
        return newSet;
      });
    }
  };

  const shouldDisplayInitialView =
    (!mediaKeyword || mediaKeyword.length <= SEARCH_LENGTH_LIMIT) &&
    !(
      (mediaType === "image" || mediaType === "images") &&
      selectedEngine === searchEngines[mediaType][1]?.key
    );

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight + 100 >= scrollHeight) {
        if (!isLoading && !isNotFound) {
          const nextPage = page + 1;
          setPage(nextPage);
          getMedia(mediaKeyword, selectedEngine, nextPage);
        }
      }
    }
  }, [mediaKeyword, selectedEngine, page, isLoading, isNotFound]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  const handleErrorMedia = (errorMedia) => {
    setSelectedMedia(selectedMedia.filter((media) => media !== errorMedia));
  };

  return (
    <Styled.Container width="100%">
      <Header
        selectedEngine={selectedEngine}
        handleEngineChange={handleEngineChange}
        handleChangeMediaKeyword={handleMediaKeywordChange}
        items={searchEngines[mediaType]}
        mediaKeyword={mediaKeyword}
        showDropdown={showDropdown}
        onSearch={handleSearch}
        mediaType={mediaType}
      />
      <Styled.MediaContainer ref={containerRef}>
        {shouldDisplayInitialView ? (
          <InitialView
            content={initialKeywords}
            handleSelectKeyword={handleInitialKeywordsClick}
          />
        ) : isNotFound ? (
          <EmptySearchContainer />
        ) : (
          <MediaMasonry
            content={selectedMedia}
            mediaKeyword={mediaKeyword}
            isLoading={isLoading}
            onReplaceButtonPress={handleSave}
            handleErrorMedia={handleErrorMedia}
            mediaType={mediaType}
            loadingMediaItems={loadingMediaItems}
          />
        )}
      </Styled.MediaContainer>
    </Styled.Container>
  );
};

export default MediaBrowser;
